import type { BillingChart } from "@scoach/types";

/**
 * Renders the CRM billing section's `charts` (Chart.js-style configs) as
 * dependency-free SVG graphs styled after the CRM's own Chart.js dashboard:
 * area-filled trend lines (Gross), dashed reference lines (RVP / promo credit),
 * money-formatted axes with gridlines, hover tooltips, and a doughnut for the
 * cost-distribution breakdown. Handles bar / line / pie|doughnut; anything else
 * falls back to bars. The caller only mounts this when `normalizeBillingCharts`
 * finds ≥1 usable chart — otherwise the markdown table is shown instead.
 */

// Google-brand palette (matches the CRM billing dashboard).
const PALETTE = [
  "#4285F4",
  "#EA4335",
  "#FBBC04",
  "#34A853",
  "#A142F4",
  "#24C1E0",
  "#FF6D01",
  "#9AA0A6",
  "#F538A0",
  "#1E8E3E",
  "#C5221F",
];

/** Reference/secondary series (rendered dashed, not area-filled). */
const isReferenceSeries = (label: string | undefined): boolean =>
  /\b(rvp|blended|credit|promo|net)\b/i.test(label ?? "");
/** Primary spend series that gets the translucent area fill. */
const isPrimarySeries = (label: string | undefined): boolean => /\bgross\b/i.test(label ?? "");

// biome-ignore lint/suspicious/noExplicitAny: charts come loosely-typed from the CRM
type Any = any;

/** Pull a numeric series out of a dataset that may store it under different keys
 * (Chart.js `data`, or `values`/`points`, possibly as {x,y}/{value} objects). */
function seriesData(ds: Any): number[] | null {
  const raw = ds?.data ?? ds?.values ?? ds?.points ?? ds?.y;
  if (!Array.isArray(raw)) return null;
  const nums = raw.map((v: Any) =>
    typeof v === "number"
      ? v
      : typeof v?.y === "number"
        ? v.y
        : typeof v?.value === "number"
          ? v.value
          : Number.parseFloat(String(v)),
  );
  return nums.some((n: number) => !Number.isNaN(n)) ? nums.map((n: number) => n || 0) : null;
}

/** Coerce a single value to a number (handles "$1,234", "12%", "3.4M"). */
function numOf(v: Any): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") return parseNum(v);
  return null;
}

const LABEL_KEYS = [
  "label",
  "name",
  "service",
  "product",
  "sku",
  "month",
  "date",
  "period",
  "category",
  "x",
  "key",
];
const META_KEYS = new Set(["labels", "categories", "x", "keys", "title", "type", "name", "index"]);

/** Build a chart from an array of row-objects, e.g. [{service, cost}] or
 * [{month, gross, rvp}]: the first non-numeric column is the x-axis, numeric
 * columns become series. */
function chartFromRows(rows: Any[], title?: string, type?: string): BillingChart | null {
  const objs = rows.filter((r) => r && typeof r === "object" && !Array.isArray(r));
  if (objs.length === 0) {
    // A bare numeric array with no labels can't be charted on its own.
    return null;
  }
  const keys = Array.from(new Set(objs.flatMap((r) => Object.keys(r))));
  const numericKeys = keys.filter(
    (k) => objs.filter((r) => numOf(r[k]) !== null).length >= Math.ceil(objs.length * 0.6),
  );
  const labelKeys = keys.filter((k) => !numericKeys.includes(k));
  if (numericKeys.length === 0) return null;
  const labelKey = LABEL_KEYS.find((lk) => labelKeys.includes(lk)) ?? labelKeys[0] ?? undefined;
  const labels = labelKey
    ? objs.map((r) => String(r[labelKey] ?? ""))
    : objs.map((_, i) => String(i + 1));
  const datasets = numericKeys.map((k) => ({
    label: k,
    data: objs.map((r) => numOf(r[k]) ?? 0),
  }));
  return { title, type, data: { labels, datasets } };
}

/** Build a chart from a columnar object, e.g. {labels:[…], gross:[…], rvp:[…]} or
 * {labels:[…], values:[…]} or {labels:[…], data:[…]}. */
function chartFromColumnar(obj: Any, title?: string, type?: string): BillingChart | null {
  const labels: Any = obj.labels ?? obj.categories ?? obj.x ?? obj.keys ?? obj.months;
  if (!Array.isArray(labels)) return null;
  const datasets: { label?: string; data: number[] }[] = [];
  for (const [k, v] of Object.entries(obj)) {
    if (v === labels || META_KEYS.has(k)) continue;
    if (!Array.isArray(v)) continue;
    const nums = (v as Any[]).map(numOf);
    if (nums.length > 0 && nums.every((n) => n !== null)) {
      datasets.push({ label: k, data: nums as number[] });
    }
  }
  if (datasets.length === 0) return null;
  return { title, type, data: { labels: labels.map(String), datasets } };
}

/** Build a chart from a plain map of name→number, e.g. {"Compute Engine": 1234,
 * "BigQuery": 567} (a common doughnut/bar shape). */
function chartFromNumberMap(obj: Any, title?: string, type?: string): BillingChart | null {
  const entries = Object.entries(obj).filter(([k]) => !META_KEYS.has(k));
  if (entries.length === 0) return null;
  const nums = entries.map(([, v]) => numOf(v));
  if (!nums.every((n) => n !== null)) return null;
  return {
    title,
    type,
    data: {
      labels: entries.map(([k]) => k),
      datasets: [{ label: title ?? "", data: nums as number[] }],
    },
  };
}

/** Build a chart from a map of key→object, e.g. {"2024-01": {gross, rvp}, …} — each
 * key becomes an x-axis label, the nested numeric fields become series. */
function chartFromObjectMap(obj: Any, title?: string, type?: string): BillingChart | null {
  const rows = Object.entries(obj)
    .filter(([k, v]) => !META_KEYS.has(k) && v && typeof v === "object" && !Array.isArray(v))
    .map(([k, v]) => ({ __label: k, ...(v as object) }));
  if (rows.length === 0) return null;
  return chartFromRows(rows, title, type);
}

/** Coerce ONE loosely-typed value into a renderable BillingChart, trying (in order): a
 * Chart.js config (optionally nested under `.config`/`.data`, datasets under
 * data/values/…), a columnar {labels + numeric arrays} object, an array of
 * row-objects, a key→object map, or a name→number map. */
function coerceChart(c: Any, title?: string, type?: string): BillingChart | null {
  if (!c || typeof c !== "object") return null;
  if (Array.isArray(c)) return chartFromRows(c, title, type);

  // Chart.js-style config (possibly nested under `.config`).
  const cfg = c.config && typeof c.config === "object" ? c.config : c;
  const d = cfg.data && typeof cfg.data === "object" ? cfg.data : cfg;
  const cjsLabels: Any = d.labels ?? cfg.labels ?? d.categories ?? d.x;
  const rawSets: Any = d.datasets ?? cfg.datasets ?? d.series ?? cfg.series;
  if (Array.isArray(cjsLabels) && Array.isArray(rawSets)) {
    const datasets = rawSets
      .map((ds: Any) => {
        const data = seriesData(ds);
        return data ? { label: ds?.label ?? ds?.name ?? ds?.title ?? "", data } : null;
      })
      .filter(Boolean) as { label?: string; data: number[] }[];
    if (datasets.length > 0) {
      return {
        title: c.title ?? cfg.title ?? title,
        type: c.type ?? cfg.type ?? type,
        data: { labels: cjsLabels as (string | number)[], datasets },
      };
    }
  }

  return (
    chartFromColumnar(c, title, type) ??
    chartFromObjectMap(c, title, type) ??
    chartFromNumberMap(c, title, type)
  );
}

/** Nice title + preferred chart type for the CRM's named billing sections. */
const NAMED_CHART_META: Record<string, { title: string; type: string }> = {
  months: { title: "Monthly spend", type: "line" },
  monthly: { title: "Monthly spend", type: "line" },
  trend: { title: "Monthly spend", type: "line" },
  by_service: { title: "Spend by service", type: "bar" },
  byservice: { title: "Spend by service", type: "bar" },
  services: { title: "Spend by service", type: "bar" },
  distribution: { title: "Cost distribution", type: "doughnut" },
  breakdown: { title: "Cost distribution", type: "doughnut" },
};
function prettyTitle(key: string): string {
  return key.replace(/[_-]+/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * The CRM's current billing payload:
 *   { months:[…labels], total:{gross,blended,credit,net:[…]},
 *     by_service:[{name,data:[…]}], distribution:{labels,values} }
 * `months` is the shared x-axis for `total` and `by_service` (which carry series but
 * no labels of their own) — so this needs structure-aware handling, not the generic
 * coercer. Returns null when `charts` isn't this shape (fall back to the generic path). */
function chartsFromCrmBilling(charts: Any): BillingChart[] | null {
  if (!charts || typeof charts !== "object" || Array.isArray(charts)) return null;
  const months = charts.months;
  if (!Array.isArray(months) || months.length === 0) return null;
  const labels = months.map(String);
  const out: BillingChart[] = [];

  // 1. Total Monthly Spend — Gross (area), RVP (blended, dashed), Net, Promo Credit.
  //    Labels/order match the CRM dashboard; all-zero series (e.g. credit) are dropped.
  const total = charts.total;
  if (total && typeof total === "object" && !Array.isArray(total)) {
    const seriesDefs: [string, string][] = [
      ["gross", "Gross"],
      ["blended", "RVP"],
      ["net", "Net (after promo credit)"],
      ["credit", "Promo Credit"],
    ];
    const datasets = seriesDefs
      .filter(([k]) => Array.isArray(total[k]) && total[k].length === labels.length)
      .map(([k, label]) => ({ label, data: (total[k] as Any[]).map((n) => numOf(n) ?? 0) }))
      .filter((d) => d.data.some((n) => n !== 0));
    if (datasets.length > 0) {
      out.push({ title: "Total monthly spend", type: "line", data: { labels, datasets } });
    }
  }

  // 2. Top services over time (Gross): by_service = [{name, data:[…]}], one line each.
  if (Array.isArray(charts.by_service)) {
    const datasets = charts.by_service
      .filter((s: Any) => s && Array.isArray(s.data))
      .map((s: Any) => ({
        label: s.name ?? s.label ?? "",
        data: (s.data as Any[]).map((n) => numOf(n) ?? 0),
      }));
    if (datasets.length > 0) {
      out.push({ title: "Top services over time", type: "line", data: { labels, datasets } });
    }
  }

  // 3. Cost distribution by service (doughnut): {labels, values}.
  const dist = charts.distribution;
  if (dist && Array.isArray(dist.labels) && Array.isArray(dist.values)) {
    out.push({
      title: "Cost distribution by service",
      type: "doughnut",
      data: {
        labels: dist.labels.map(String),
        datasets: [{ label: "Cost", data: dist.values.map((n: Any) => numOf(n) ?? 0) }],
      },
    });
  }

  return out.length > 0 ? out : null;
}

/** Coerce the loosely-typed `charts` field into an array of usable BillingCharts.
 * Handles: the CRM's named-object shape ({months, total, by_service, distribution});
 * an array of Chart.js configs; a record of them; a single chart object; and other
 * tabular/columnar/map entries. */
export function normalizeBillingCharts(raw: unknown): BillingChart[] {
  if (!raw || typeof raw !== "object") return [];

  // The CRM's structured billing shape (months = shared x-axis) — handle first.
  const crm = chartsFromCrmBilling(raw as Any);
  if (crm) return crm;

  // A single, already-well-formed chart object.
  if (!Array.isArray(raw)) {
    const single = coerceChart(raw);
    if (single) return [single];
  }

  const out: BillingChart[] = [];
  const entries: [string, unknown][] = Array.isArray(raw)
    ? raw.map((v, i) => [String(i), v])
    : Object.entries(raw as Record<string, unknown>);
  for (const [key, v] of entries) {
    // `total` is a KPI scalar/object, not a chart — don't force it into one.
    if (key.toLowerCase() === "total") continue;
    const meta = NAMED_CHART_META[key.toLowerCase()];
    const chart = coerceChart(v, meta?.title ?? prettyTitle(key), meta?.type);
    if (chart) out.push(chart);
  }
  return out;
}

/** Parse a currency/number cell → number. Handles $, commas, %, and K/M/B/T suffixes. */
function parseNum(cell: string): number | null {
  const raw = cell
    .trim()
    .replace(/[$,%\s]/g, "")
    .replace(/,/g, "");
  const m = raw.match(/^(-?\d*\.?\d+)\s*([kmbt])?$/i);
  if (!m) return null;
  let v = Number.parseFloat(m[1]!);
  if (Number.isNaN(v)) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 }[(m[2] ?? "").toLowerCase()];
  if (mult) v *= mult;
  return v;
}

const TEMPORAL =
  /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|q[1-4]|\d{4}|\d{1,2}\/\d{2,4}|\d{4}-\d{2})/i;

const isSep = (l: string): boolean => /^\|?[\s:|-]+\|?$/.test(l) && l.includes("-");
const temporalLabels = (labels: string[]): boolean =>
  labels.length > 0 && labels.every((l) => TEMPORAL.test(l));
const uniqueInOrder = (arr: string[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of arr) {
    if (!seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  }
  return out;
};
/** Split a table row into cells — supports pipe tables and whitespace/tab-separated
 * tables (2+ spaces or a tab between columns, so multi-word cells stay intact). */
const splitCells = (line: string): string[] => {
  const s = line.trim();
  if (s.includes("|")) {
    return s
      .replace(/^\|/, "")
      .replace(/\|$/, "")
      .split("|")
      .map((c) => c.trim());
  }
  return s
    .split(/\t+| {2,}/)
    .map((c) => c.trim())
    .filter((c) => c.length > 0);
};
const modeCount = (nums: number[]): number => {
  const freq = new Map<number, number>();
  for (const n of nums) freq.set(n, (freq.get(n) ?? 0) + 1);
  let best = 0;
  let bestN = 0;
  for (const [n, f] of freq) if (f > best || (f === best && n > bestN)) [best, bestN] = [f, n];
  return bestN;
};

/**
 * Extract data tables from a billing report and turn each into BillingChart(s). Handles
 * both GFM pipe tables and whitespace/tab-separated tables. The first non-numeric column
 * is the x-axis; mostly-numeric columns are series. For long/pivot tables with TWO
 * dimension columns (e.g. Month × Service × Gross/RVP) it pivots — one chart per numeric
 * measure, a series per distinct 2nd-dimension value. Temporal x-labels → line, else bars.
 */
export function parseMarkdownTables(md: string): BillingChart[] {
  if (!md) return [];
  const lines = md.split("\n");
  const charts: BillingChart[] = [];
  let title = "";

  for (let i = 0; i < lines.length; ) {
    const line = lines[i]!.trim();
    if (!line || isSep(line)) {
      i++;
      continue;
    }
    const firstCells = splitCells(line);
    if (firstCells.length < 2) {
      // A short prose line just before a table is used as its title.
      title = line
        .replace(/^#{1,6}\s*/, "")
        .replace(/\*\*/g, "")
        .replace(/^\d+\.\s*/, "")
        .trim();
      i++;
      continue;
    }
    // Collect the contiguous run of table-like lines.
    const tbl: string[][] = [];
    let j = i;
    for (; j < lines.length; j++) {
      const lj = lines[j]!.trim();
      if (!lj) break;
      if (isSep(lj)) continue;
      const cj = splitCells(lj);
      if (cj.length < 2) break;
      tbl.push(cj);
    }
    i = j;

    const colCount = modeCount(tbl.map((r) => r.length));
    const all = tbl.filter((r) => r.length === colCount);
    if (all.length < 3 || colCount < 2) {
      title = "";
      continue;
    }
    const header = all[0]!;
    const rows = all.slice(1);
    const numeric = header.map((_, c) => {
      const good = rows.filter((r) => parseNum(r[c] ?? "") !== null).length;
      return good >= Math.max(2, Math.ceil(rows.length * 0.5));
    });
    const measures = header.map((_, c) => c).filter((c) => numeric[c]);
    const dims = header.map((_, c) => c).filter((c) => !numeric[c]);
    if (measures.length === 0) {
      title = "";
      continue;
    }
    const baseTitle = title || header[0] || "Billing";

    if (dims.length >= 2) {
      // Pivot: x = dims[0], series = dims[1], one chart per numeric measure.
      const xCol = dims[0]!;
      const sCol = dims[1]!;
      const xLabels = uniqueInOrder(rows.map((r) => r[xCol] ?? ""));
      const seriesVals = uniqueInOrder(rows.map((r) => r[sCol] ?? ""));
      for (const mCol of measures) {
        const datasets = seriesVals.map((sv) => ({
          label: sv,
          data: xLabels.map((x) => {
            const hit = rows.find((r) => r[xCol] === x && r[sCol] === sv);
            return hit ? (parseNum(hit[mCol] ?? "") ?? 0) : 0;
          }),
        }));
        charts.push({
          title: `${baseTitle} — ${header[mCol]} by ${header[sCol]}`,
          type: temporalLabels(xLabels) ? "line" : "bar",
          data: { labels: xLabels, datasets },
        });
      }
    } else {
      const labelCol = dims[0] ?? 0;
      const labels = rows.map((r) => r[labelCol] ?? "");
      const datasets = measures
        .filter((c) => c !== labelCol)
        .map((c) => ({ label: header[c], data: rows.map((r) => parseNum(r[c] ?? "") ?? 0) }));
      if (datasets.length > 0) {
        charts.push({
          title: baseTitle,
          type: temporalLabels(labels) ? "line" : "bar",
          data: { labels, datasets },
        });
      }
    }
    title = "";
  }
  return charts;
}

/** Full money label, e.g. $1,234,567 (tooltips). */
const money = (n: number): string => `$${Math.round(n).toLocaleString()}`;
/** Compact money label for axis ticks, e.g. $1.2M / $12K. */
function moneyShort(n: number): string {
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

/** Round up to a "nice" axis maximum (1/2/5 × 10ⁿ). */
function niceCeil(x: number): number {
  if (x <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(x));
  const f = x / p;
  const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nf * p;
}

/** Catmull-Rom-ish smoothing → a cubic-bezier path through the points (tension ~0.18). */
function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length === 0) return "";
  if (pts.length < 3) return `M${pts.map((p) => `${p.x} ${p.y}`).join(" L")}`;
  const t = 0.18;
  let d = `M${pts[0]!.x} ${pts[0]!.y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) * t;
    const c1y = p1.y + (p2.y - p0.y) * t;
    const c2x = p2.x - (p3.x - p1.x) * t;
    const c2y = p2.y - (p3.y - p1.y) * t;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2.x} ${p2.y}`;
  }
  return d;
}

const AXIS = "var(--border, #e0e0e0)";
const GRID = "var(--border-soft, #eef0f3)";
const TXT = "var(--text-4, #9aa0a6)";

/** Line / bar chart with gridlines, money axis, area fill (Gross) and dashed
 * reference series (RVP / promo credit), plus per-point hover tooltips. */
function AxisChart({ chart, line }: { chart: BillingChart; line: boolean }) {
  const W = 640;
  const H = 300;
  const pad = { l: 60, r: 18, t: 12, b: 34 };
  const labels = (chart.data?.labels ?? []).map((l) => String(l));
  const datasets = (chart.data?.datasets ?? []).filter((d) => Array.isArray(d.data));
  const all = datasets.flatMap((d) => d.data);
  const maxTick = niceCeil(Math.max(1, ...all));
  const n = labels.length || 1;
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  const xs = (i: number) => pad.l + (n === 1 ? plotW / 2 : (i * plotW) / (n - 1));
  const xsBar = (i: number) => pad.l + (i + 0.5) * (plotW / n);
  const ys = (v: number) => pad.t + plotH - (v / maxTick) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => maxTick * f);
  // Thin x labels so they don't overlap on long series.
  const labelStep = Math.ceil(n / 12);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      style={{ height: "auto", display: "block" }}
      role="img"
      aria-label={chart.title ?? "chart"}
    >
      {/* gridlines + money ticks */}
      {ticks.map((v) => (
        <g key={v}>
          <line x1={pad.l} y1={ys(v)} x2={W - pad.r} y2={ys(v)} stroke={GRID} strokeWidth={1} />
          <text x={pad.l - 8} y={ys(v) + 3} fontSize={10} fill={TXT} textAnchor="end">
            {moneyShort(v)}
          </text>
        </g>
      ))}
      <line x1={pad.l} y1={pad.t} x2={pad.l} y2={H - pad.b} stroke={AXIS} strokeWidth={1} />

      {datasets.map((ds, di) => {
        const color = PALETTE[di % PALETTE.length]!;
        if (line) {
          const pts = ds.data.map((v, i) => ({ x: xs(i), y: ys(v) }));
          const dash = isReferenceSeries(ds.label);
          const fill =
            isPrimarySeries(ds.label) ||
            (di === 0 && !datasets.some((d) => isPrimarySeries(d.label)));
          const linePath = smoothPath(pts);
          return (
            <g key={ds.label ?? di}>
              {fill && pts.length > 1 && (
                <path
                  d={`${linePath} L${pts[pts.length - 1]!.x} ${ys(0)} L${pts[0]!.x} ${ys(0)} Z`}
                  fill={color}
                  fillOpacity={0.12}
                  stroke="none"
                />
              )}
              <path
                d={linePath}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeDasharray={dash ? "5 4" : undefined}
                strokeLinejoin="round"
              />
              {pts.map((p, i) => (
                <circle
                  // biome-ignore lint/suspicious/noArrayIndexKey: fixed positional points
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={6}
                  fill="transparent"
                >
                  <title>{`${labels[i] ?? ""} · ${ds.label ?? ""}: ${money(ds.data[i] ?? 0)}`}</title>
                </circle>
              ))}
            </g>
          );
        }
        const bw = (plotW / n / datasets.length) * 0.8;
        return ds.data.map((v, i) => (
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positional bars
            key={`${di}-${i}`}
            x={xsBar(i) - (bw * datasets.length) / 2 + di * bw}
            y={ys(Math.max(0, v))}
            width={bw}
            height={Math.abs(ys(v) - ys(0))}
            fill={color}
            rx={1.5}
          >
            <title>{`${labels[i] ?? ""} · ${ds.label ?? ""}: ${money(v)}`}</title>
          </rect>
        ));
      })}

      {labels.map((l, i) =>
        i % labelStep === 0 ? (
          <text
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positional labels
            key={i}
            x={line ? xs(i) : xsBar(i)}
            y={H - pad.b + 16}
            fontSize={10}
            fill={TXT}
            textAnchor="middle"
          >
            {l.length > 10 ? `${l.slice(0, 9)}…` : l}
          </text>
        ) : null,
      )}
    </svg>
  );
}

/** Doughnut for the cost-distribution breakdown, with a right-side value legend. */
function Doughnut({ chart }: { chart: BillingChart }) {
  const labels = (chart.data?.labels ?? []).map((l) => String(l));
  const values = chart.data?.datasets?.[0]?.data ?? [];
  const total = values.reduce((a, b) => a + (b || 0), 0);
  const cx = 130;
  const cy = 130;
  const r = 108;
  const inner = r * 0.6;
  let angle = -Math.PI / 2; // start at 12 o'clock

  const arcs = values.map((v, i) => {
    const frac = total > 0 ? v / total : 0;
    const a0 = angle;
    const a1 = angle + frac * Math.PI * 2;
    angle = a1;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const p = (rad: number, a: number) => `${cx + rad * Math.cos(a)} ${cy + rad * Math.sin(a)}`;
    const d = `M${p(r, a0)} A${r} ${r} 0 ${large} 1 ${p(r, a1)} L${p(inner, a1)} A${inner} ${inner} 0 ${large} 0 ${p(inner, a0)} Z`;
    return { d, color: PALETTE[i % PALETTE.length]!, label: labels[i] ?? "", value: v || 0, frac };
  });

  return (
    <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
      <svg
        viewBox="0 0 260 260"
        width={220}
        height={220}
        role="img"
        aria-label={chart.title ?? "distribution"}
      >
        {total > 0 ? (
          arcs.map((a, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed slice order
            <path key={i} d={a.d} fill={a.color} stroke="var(--surface, #fff)" strokeWidth={1.5}>
              <title>{`${a.label}: ${money(a.value)} (${(a.frac * 100).toFixed(1)}%)`}</title>
            </path>
          ))
        ) : (
          <circle cx={cx} cy={cy} r={r} fill={GRID} />
        )}
        <text x={cx} y={cy - 4} fontSize={13} fill="var(--text-3, #5f6368)" textAnchor="middle">
          Total
        </text>
        <text
          x={cx}
          y={cy + 16}
          fontSize={16}
          fontWeight={600}
          fill="var(--text-1, #202124)"
          textAnchor="middle"
        >
          {moneyShort(total)}
        </text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160, flex: 1 }}>
        {arcs.map((a, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: legend order is stable
            key={i}
            style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}
          >
            <span
              style={{ width: 10, height: 10, borderRadius: 2, background: a.color, flexShrink: 0 }}
            />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {a.label}
            </span>
            <span style={{ color: "var(--text-3, #5f6368)", fontVariantNumeric: "tabular-nums" }}>
              {money(a.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Legend({ chart }: { chart: BillingChart }) {
  const datasets = (chart.data?.datasets ?? []).filter((d) => d.label);
  if (datasets.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8 }}>
      {datasets.map((d, i) => (
        <span
          // biome-ignore lint/suspicious/noArrayIndexKey: legend order is stable
          key={i}
          style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}
        >
          <span
            style={{
              width: 14,
              height: 3,
              borderRadius: 2,
              background: PALETTE[i % PALETTE.length],
              opacity: isReferenceSeries(d.label) ? 0.6 : 1,
            }}
          />
          {d.label}
        </span>
      ))}
    </div>
  );
}

const isPieish = (t: string | undefined): boolean => t === "pie" || t === "doughnut";

export function BillingCharts({ charts }: { charts: BillingChart[] }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
        gap: 16,
      }}
    >
      {charts.map((chart, i) => {
        const pie = isPieish(chart.type);
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: charts have no stable id
            key={i}
            style={{
              gridColumn: pie ? "1 / -1" : undefined,
              background: "var(--surface, #fff)",
              border: `1px solid ${AXIS}`,
              borderRadius: 10,
              padding: 14,
            }}
          >
            {chart.title && (
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{chart.title}</div>
            )}
            {pie ? (
              <Doughnut chart={chart} />
            ) : (
              <>
                <AxisChart chart={chart} line={chart.type === "line"} />
                <Legend chart={chart} />
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
